/*
 * ez-product-rail — the Product rail EXTRAS on top of the shared ez-carousel engine.
 *
 * Transport (desktop arrows / mobile dots-or-progress / direction-aware step / scroll sync) is the
 * ONE ez-carousel.js engine (design_system.md §6 Carousel cluster). This subclass adds ONLY what the
 * rail needs beyond a plain carousel:
 *   - native product-recommendations fetch-and-swap (the "Related products" preset),
 *   - interactive colour swatches (tap swaps the card hero + its alt + the hover 2nd-angle),
 *   - adaptive swatch density (fold overflow into "+N"; re-fit on resize),
 *   - hover-intent load of each card's 2nd-angle image (never requested upfront — PK data cost).
 *
 * extends window.EzCarousel (ez-carousel.js is emitted FIRST; defer preserves document order). One
 * vanilla custom element, no framework and no external slider library (Part A §A5 / §A7).
 */
(function () {
  if (!window.EzCarousel || customElements.get('ez-product-rail')) return;

  class EzProductRail extends window.EzCarousel {
    connectedCallback() {
      // Recommendations source: fetch + swap first, THEN boot the engine. Any other source boots now.
      var url = this.dataset.ezRecommendationsUrl;
      if (url) {
        this._loadRecommendations(url);
      } else {
        this._boot();
      }
    }

    // Native product-recommendations source (design_system.md §8 Type 2 "Related products"
    // preset). Fetch the section rendered through routes.product_recommendations_url, swap in
    // its rail markup, then boot the engine. Deferred to near-viewport so a below-fold PDP rail
    // costs no upfront request; if there are no recommendations, collapse the section.
    _loadRecommendations(url) {
      var self = this;
      var run = function () {
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (text) {
            var parsed = document.createElement('div');
            parsed.innerHTML = text;
            // Identify OURSELVES in the response by block id, not by "the first rail in the
            // document". Once the rail is a block inside the shared ez-section shell, the
            // Section Rendering API returns the WHOLE section, which may hold more than one
            // rail — picking the first would swap the wrong one's markup in. The block id is
            // stable across the round-trip, so it is the only safe selector. Falls back to the
            // old behaviour for any surface still rendering the rail as its own section.
            var myId = self.dataset.ezRailId;
            var fresh = myId
              ? parsed.querySelector('ez-product-rail[data-ez-rail-id="' + myId + '"]')
              : null;
            if (!fresh) fresh = parsed.querySelector('ez-product-rail');
            if (fresh && fresh.innerHTML.trim().length) {
              self.innerHTML = fresh.innerHTML;
              self.removeAttribute('data-ez-recommendations-url');
              self._boot();
            } else {
              self._collapse();
            }
          })
          .catch(function () { self._collapse(); });
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries, obs) {
          if (entries[0].isIntersecting) { obs.disconnect(); run(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else {
        run();
      }
    }

    // No recommendations -> hide the whole section rather than leave an orphan heading.
    // (.ez-product-rail carries no author `display`, so [hidden] takes effect — see bug-054.)
    _collapse() {
      // Hide the RAIL, never the shared shell. As a block inside ez-section the shell may hold
      // sibling content that must survive; killing the section would take the siblings with it.
      // The shell hides ITSELF in CSS when every content block inside it is hidden, so an
      // orphan heading still cannot happen — see ez-section.liquid's empty-shell rule.
      var el = this.closest('[data-ez-section-content]') || this.closest('.ez-product-rail');
      if (el) el.hidden = true;
    }

    // Engine hook: the transport controls are wired; now wire the rail's extras. The engine has
    // already resolved this.cells (from data-ez-cell = ".ez-card") before calling this.
    _afterBoot() {
      // Curated cards render THEMSELVES, and Shopify does not expose a nested block's settings to
      // its parent — so the rail's card-display choices are applied from the OUTSIDE here, before
      // anything else reads the card. (show_hover / show_swatches are pure CSS on the rail root.)
      this._applyCardSettings();

      // Hover-intent: load the 2nd-angle image once, on first pointer entry (desktop only).
      if (window.matchMedia('(hover: hover)').matches) {
        this.cells.forEach(function (cell) {
          var swap = cell.querySelector('.ez-card__img--hover');
          if (!swap || !swap.dataset.ezHoverSrc) return;
          var load = function () {
            swap.src = swap.dataset.ezHoverSrc;
            cell.removeEventListener('pointerenter', load);
          };
          cell.addEventListener('pointerenter', load);
        });
      }

      this._wireSwatches();
      this._fitSwatches();
    }

    // Engine hook: on resize the engine re-syncs the transport controls; the rail also re-fits
    // the adaptive swatch density.
    _afterResize() {
      this._fitSwatches();
    }

    // OUTSIDE-IN card settings (the parent/child fix). The rail carries its card-display choices
    // as data-ez-card-* attributes on itself; this reads them back onto the cards. Two of the four
    // land here (default angle + New badge); swatch density lands in _fitSwatches, which already
    // owns swatch counts. Idempotent, and a no-op on a dynamic card that Liquid already rendered
    // at the right angle — the values it writes are the ones Liquid would have written.
    _applyCardSettings() {
      var angle = parseInt(this.dataset.ezCardAngle || '0', 10) || 0;
      var noBadge = this.hasAttribute('data-ez-card-no-badge');
      var newDays = parseInt(this.dataset.ezCardNewDays || '30', 10) || 30;
      var nowSec = Math.floor(Date.now() / 1000);

      this.cells.forEach(function (card) {
        // ---- Default image angle -----------------------------------------
        // data-ez-angles is "url1|url2|url3|" (locked upload order hero,front,temple,folded).
        // An empty slot means the product has no such angle -> stay on the hero.
        if (angle > 0) {
          // Only ever trust something that LOOKS like a URL. Liquid's image_url over a nil media
          // entry returns an error STRING rather than a blank (bug-166), and an error string in
          // an <img> src is a broken image on a customer's screen.
          var ok = function (u) { return !!u && u.indexOf('//') !== -1; };
          var hero = card.querySelector('.ez-card__img--hero');
          if (hero && hero.dataset.ezAngles) {
            var url = hero.dataset.ezAngles.split('|')[angle - 1];
            if (ok(url)) {
              hero.removeAttribute('srcset'); // srcset would win over the new src
              hero.src = url;
            }
          }
          // The swatches carry their own per-variant angle list, so a swatch tap keeps showing the
          // chosen angle instead of snapping back to that variant's hero.
          card.querySelectorAll('[data-ez-card-swatch][data-ez-angles]').forEach(function (sw) {
            var u = sw.getAttribute('data-ez-angles').split('|')[angle - 1];
            if (ok(u)) sw.setAttribute('data-ez-hero', u);
          });
        }

        // ---- "New" badge + its age window --------------------------------
        // Priority is unchanged (sold-out > new, max one badge): an existing badge is never
        // replaced, so a sold-out card keeps its sold-out badge.
        var badge = card.querySelector('.ez-card__badge');
        if (noBadge) {
          if (badge && badge.classList.contains('ez-card__badge--new')) badge.remove();
          return;
        }
        if (badge) return;
        var created = parseInt(card.dataset.ezCreated || '', 10);
        if (!created) return;
        var age = nowSec - created;
        if (age < 0 || age > newDays * 86400) return;
        var media = card.querySelector('.ez-card__media');
        if (!media) return;
        var el = document.createElement('span');
        el.className = 'ez-card__badge ez-card__badge--new';
        el.textContent = 'New';
        media.appendChild(el);
      });
    }

    // C-3 [v1.3] interactive swatches: tapping a swatch moves the selection ring and swaps the
    // card hero to that variant's image (the row sits OUTSIDE the card link, so no nested anchor).
    // per-variant-images A1: it ALSO re-points the 2nd/hover image to the SAME variant's front
    // angle, so the hover swap follows the selected colour instead of staying product-level.
    _wireSwatches() {
      this.cells.forEach(function (card) {
        var row = card.querySelector('[data-ez-card-swatches]');
        if (!row) return;
        var hero = card.querySelector('.ez-card__img--hero');
        var hover = card.querySelector('.ez-card__img--hover');
        row.addEventListener('click', function (e) {
          var sw = e.target.closest('[data-ez-card-swatch]');
          if (!sw) return;
          row.querySelectorAll('[data-ez-card-swatch]').forEach(function (s) {
            s.classList.remove('is-selected');
            s.setAttribute('aria-pressed', 'false');
          });
          sw.classList.add('is-selected');
          sw.setAttribute('aria-pressed', 'true');
          var src = sw.getAttribute('data-ez-hero');
          if (hero && src) {
            hero.removeAttribute('srcset'); // srcset would override the new src otherwise
            hero.src = src;
            // a11y: keep the hero's alt in sync with the swapped image so the accessible name
            // reflects the selected colour (data-ez-alt carries the variant hero's locked
            // `<title> <color> <shape> <type>` alt). Leave the alt untouched if absent.
            var alt = sw.getAttribute('data-ez-alt');
            if (alt !== null) hero.alt = alt;
          }
          // Re-point the hover image to this variant's 2nd angle. Update the
          // lazy source so the NEXT hover loads the right angle; if the hover
          // image was already loaded, refresh it now so the current hover is
          // correct too. [bug-146 rail] FALL BACK to this swatch's hero image
          // (data-ez-hero) when it carries no distinct 2nd angle (data-ez-hover
          // absent): a swatch tap must ALWAYS re-point the hover to the SELECTED
          // variant (its 2nd angle if present, else its own hero) and NEVER leave
          // it on the default variant's angle — the reported wrong-colour-on-hover
          // symptom. data-ez-hero is present on every swatch that swaps, so this
          // guarantees the invariant regardless of per-variant 2nd-image data.
          var hoverSrc = sw.getAttribute('data-ez-hover') || sw.getAttribute('data-ez-hero');
          if (hover && hoverSrc) {
            hover.dataset.ezHoverSrc = hoverSrc;
            if (hover.src) hover.src = hoverSrc;
          }
        });
      });
    }

    // C-3 [v1.3] adaptive density: the visible swatch count follows the card WIDTH. Reveal all,
    // then hide from the end to fit, folding the overflow (+ any colours beyond the server cap)
    // into the "+N" chip. Runs on boot and resize.
    _fitSwatches() {
      var PITCH = 28; // 20px chip + 8px gap (A2: small card-appropriate swatch)
      // [outside-in] the rail's "Colour swatches per card". A curated card is rendered with the
      // SUPERSET (6, the setting's schema max) because JS can only ever hide, never reveal, so the
      // cap is applied here. 0 / absent = no cap (a dynamic card was already capped server-side).
      var railMax = parseInt(this.dataset.ezCardSwatchMax || '0', 10) || 0;
      this.cells.forEach(function (card) {
        var row = card.querySelector('[data-ez-card-swatches]');
        if (!row) return;
        var lis = Array.prototype.slice.call(row.children);
        var moreEl = row.querySelector('[data-ez-swatch-more]');
        var swatchLis = lis.filter(function (li) { return li.querySelector('[data-ez-card-swatch]'); });
        if (!swatchLis.length) return;
        var extra = parseInt(row.getAttribute('data-ez-swatch-extra') || '0', 10);
        swatchLis.forEach(function (li) { li.hidden = false; });

        var avail = row.clientWidth;
        var hidden = extra; // colours already dropped server-side (beyond the cap)
        var show = swatchLis.length;
        // WIDTH trim reserves one slot for the "+N" chip (it costs real width on the row).
        if (avail) {
          var fits = Math.max(1, Math.floor((avail + 8) / PITCH));
          if (swatchLis.length > fits) show = Math.max(1, fits - 1);
        }
        // The RAIL CAP does not reserve — "4 swatches per card" must show 4, then "+N", exactly
        // as the server-side `limit: swatch_max` used to.
        if (railMax > 0 && show > railMax) show = railMax;
        for (var i = show; i < swatchLis.length; i++) { swatchLis[i].hidden = true; hidden++; }
        if (moreEl) {
          moreEl.hidden = hidden <= 0;
          if (hidden > 0) moreEl.textContent = '+' + hidden;
        }
      });
    }
  }

  customElements.define('ez-product-rail', EzProductRail);
})();
